import React, { useState } from 'react';
import { getBase64 } from '../../helpers/base64';
import { DocumentIcon } from '../../assets';

interface CVUploadProps {
  onUpload: (files: { content: string }[]) => void;
  isLoading?: boolean;
  maxFiles?: number;
}

const CVUpload: React.FC<CVUploadProps> = ({
  onUpload,
  isLoading = false,
  maxFiles = 5,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>('');
  const [isDragOver, setIsDragOver] = useState(false);

  const addFiles = (files: File[]) => {
    setError('');
    if (selectedFiles.length + files.length > maxFiles) {
      setError(`You can upload a maximum of ${maxFiles} files at a time`);
      return;
    }
    const validFiles = files.filter((file) => {
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are allowed');
        return false;
      }
      if (file.size > 2 * 1024 * 1024) {
        setError('File size must be less than 2MB');
        return false;
      }
      return true;
    });
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    if (isLoading || selectedFiles.length >= maxFiles) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (isLoading || selectedFiles.length >= maxFiles) return;
    const droppedFiles = Array.from(e.dataTransfer.files).filter((f) =>
      f.type === 'application/pdf'
    );
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setError('');
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one file');
      return;
    }

    try {
      const base64Files = await Promise.all(
        selectedFiles.map(async (file) => {
          const base64 = await getBase64(file);
          return { content: base64 as string };
        })
      );
      onUpload(base64Files);
      setSelectedFiles([]);
    } catch (err) {
      setError('Failed to process files. Please try again.');
    }
  };

  return (
    <div className="w-full my-4">
      {selectedFiles.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 flex-1">
              {selectedFiles.slice(0, 3).map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm flex-shrink-0"
                >
                  <DocumentIcon className="w-4 h-4" />
                  <span className="truncate max-w-[150px]">{file.name.replace('.pdf', ' - CV')}</span>
                  <button
                    onClick={() => handleRemoveFile(index)}
                    className="ml-1 text-white hover:text-gray-200"
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedFiles.length > 3 && (
                <span className="text-sm text-gray-600">
                  +{selectedFiles.length - 3} more
                </span>
              )}
            </div>
          </div>
          <div className="relative w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${(selectedFiles.length / maxFiles) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div className="relative">
        <label
          htmlFor="cv-upload"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center w-full px-6 py-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
            isLoading || selectedFiles.length >= maxFiles
              ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
              : isDragOver
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
          }`}
        >
          <input
            id="cv-upload"
            type="file"
            accept=".pdf"
            multiple
            onChange={handleFileChange}
            className="hidden"
            disabled={isLoading || selectedFiles.length >= maxFiles}
          />
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <p className="text-sm text-gray-600 text-center">
            <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
          </p>
          <p className="text-xs text-gray-500 mt-1">
            PDF only, max 2MB each (up to {maxFiles} files)
          </p>
        </label>

        {error && (
          <p className="mt-2 text-sm text-red-600">{error}</p>
        )}
      </div>

      {selectedFiles.length > 0 && (
        <button
          onClick={handleUpload}
          disabled={isLoading}
          className="mt-4 w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm"
        >
          {isLoading ? 'Uploading...' : `Upload ${selectedFiles.length} CV${selectedFiles.length > 1 ? 's' : ''}`}
        </button>
      )}
    </div>
  );
};

export { CVUpload };
